import React, { useMemo, useState } from 'react';
import { EyeIcon, LoaderCircleIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { useData } from '../../store/DataContext';
import { SalesReceipt } from '../../types';
import { SalesReceiptForm } from './SalesReceiptForm';
import { SalesReceiptDocumentAction, SalesReceiptModal } from './SalesReceiptModal';
import { formatCurrency } from './utils';
export function SalesReceipts() {
  const {
    currentUser,
    deleteSalesReceipt,
    salesReceipts
  } = useData();
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState<SalesReceipt | null>(null);
  const [previewAction, setPreviewAction] = useState<SalesReceiptDocumentAction>('view');
  const [deletingReceiptId, setDeletingReceiptId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const isAdministrator = currentUser?.role === 'Administrator';
  const filteredReceipts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return [...salesReceipts].filter(receipt => !query || receipt.salesReceiptNumber.toLowerCase().includes(query) || receipt.billTo.toLowerCase().includes(query) || receipt.customerPhone?.toLowerCase().includes(query) || receipt.salespersonName.toLowerCase().includes(query)).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [salesReceipts, searchQuery]);
  const openReceipt = (receipt: SalesReceipt, action: SalesReceiptDocumentAction = 'view') => {
    setPreviewAction(action);
    setSelectedReceipt(receipt);
  };
  const handleDelete = async (receipt: SalesReceipt) => {
    const confirmed = window.confirm(`Delete sales receipt ${receipt.salesReceiptNumber}? This permanently removes the cash-sale record and cannot be undone.`);
    if (!confirmed) return;
    setDeleteError(null);
    setDeletingReceiptId(receipt.id);
    const result = await deleteSalesReceipt(receipt.id);
    setDeletingReceiptId(null);
    if (!result.ok) {
      setDeleteError(result.error || 'Sales receipt could not be deleted.');
      return;
    }
    if (selectedReceipt?.id === receipt.id) setSelectedReceipt(null);
  };
  if (isCreating) {
    return <SalesReceiptForm onCancel={() => setIsCreating(false)} onSaved={(receipt, action) => {
      setIsCreating(false);
      openReceipt(receipt, action);
    }} data-id="element-1974" />;
  }
  return <div className="space-y-6" data-id="element-1975">
      <section className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-1976">
        <div data-id="element-1977">
          <p className="text-sm font-semibold text-pink-600" data-id="element-1978">
            Cashflow workspace
          </p>
          <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-950" data-id="element-1979">
            Sales Receipts
          </h2>
          <p className="mt-1 text-sm text-slate-600" data-id="element-1980">
            Independent non-invoice receipts for completed cash sales.
          </p>
        </div>
        <Button onClick={() => setIsCreating(true)} data-id="element-1981">
          <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-1982" /> New Sales Receipt
        </Button>
      </section>

      <Card noPadding data-id="element-1983">
        <div className="border-b border-slate-200 bg-slate-50 p-4" data-id="element-1984">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-1985">
            <div className="relative w-full max-w-md" data-id="element-1986">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-1987" />
              <input type="search" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Search number, bill to, phone, or sales person..." className="w-full rounded-md border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100" aria-label="Search sales receipts" data-id="element-1988" />
            </div>
            <p className="text-sm text-slate-500" data-id="element-1989">
              {salesReceipts.length} receipt
              {salesReceipts.length === 1 ? '' : 's'}
            </p>
          </div>
          {deleteError && <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-id="element-1990">
              {deleteError}
            </p>}
        </div>

        <div className="overflow-x-auto" data-id="element-1991">
          <table className="min-w-[980px] w-full divide-y divide-slate-200" data-id="element-1992">
            <thead className="bg-slate-50" data-id="element-1993">
              <tr data-id="element-1994">
                {['Visit Date', 'Number', 'Bill to', 'Phone', 'Sales Person', 'Amount', ''].map(heading => <th key={heading || 'actions'} scope="col" className={`px-5 py-3 text-xs font-bold uppercase tracking-wide text-slate-500 ${heading === 'Amount' || !heading ? 'text-right' : 'text-left'}`} data-id="element-1995">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white" data-id="element-1996">
              {filteredReceipts.length ? filteredReceipts.map(receipt => <tr key={receipt.id} className="hover:bg-slate-50/70" data-id="element-1997">
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600" data-id="element-1998">
                      {formatDate(receipt.visitDate)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 font-mono text-sm font-semibold text-blue-700" data-id="element-1999">
                      {receipt.salesReceiptNumber}
                    </td>
                    <td className="px-5 py-3.5 text-sm font-semibold text-slate-900" data-id="element-2000">
                      {receipt.billTo}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-sm text-slate-600" data-id="element-2001">
                      {receipt.customerPhone || '—'}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-2002">
                      {receipt.salespersonName}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-semibold text-slate-950" data-id="element-2003">
                      {formatCurrency(receipt.totalAmount)}
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-2004">
                      <button type="button" onClick={() => openReceipt(receipt)} className="rounded p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={`View sales receipt ${receipt.salesReceiptNumber}`} title="View sales receipt" data-id="element-2005">
                        <EyeIcon className="h-4 w-4" data-id="element-2006" />
                      </button>
                      {isAdministrator && <button type="button" onClick={() => void handleDelete(receipt)} disabled={deletingReceiptId === receipt.id} className="ml-1 rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-50" aria-label={`Delete sales receipt ${receipt.salesReceiptNumber}`} title="Delete sales receipt" data-id="element-2007">
                          {deletingReceiptId === receipt.id ? <LoaderCircleIcon className="h-4 w-4 animate-spin" data-id="element-2008" /> : <Trash2Icon className="h-4 w-4" data-id="element-2009" />}
                        </button>}
                    </td>
                  </tr>) : <tr data-id="element-2010">
                  <td colSpan={7} className="px-5 py-14 text-center" data-id="element-2011">
                    <p className="text-sm font-semibold text-slate-700" data-id="element-2012">
                      {searchQuery ? 'No matching sales receipts' : 'No sales receipts yet'}
                    </p>
                    <p className="mt-1 text-sm text-slate-500" data-id="element-2013">
                      {searchQuery ? 'Try a different receipt number, customer, or sales person.' : 'Create a receipt for a completed non-invoice cash sale.'}
                    </p>
                    {!searchQuery && <Button size="sm" className="mt-4" onClick={() => setIsCreating(true)} data-id="element-2014">
                        <PlusIcon className="mr-1.5 h-4 w-4" data-id="element-2015" /> New Sales
                        Receipt
                      </Button>}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedReceipt && <SalesReceiptModal receipt={selectedReceipt} initialAction={previewAction} onClose={() => setSelectedReceipt(null)} data-id="element-2016" />}
    </div>;
}
function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}